import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import baseapi from '../services/baseapi';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
}

interface ChatAssistantProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ChatAssistant: React.FC<ChatAssistantProps> = ({ isOpen, onClose }) => {
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            text: "Hello! I'm your OvulScanner AI Assistant. I can help you with:\n\n• **Network Monitoring & Topology** - device connections and troubleshooting\n\n• **Asset Discovery** - switches, firewalls, cameras, and endpoints\n\n• **Vulnerability Management** - patches, security issues, and remediation\n\n• **SNMP & Network Devices** - configurations and monitoring\n\n• **Camera Surveillance** - status and connectivity\n\n• **Agent Deployment** - IoT Gateway and agent installation\n\n• **Compliance & Reporting** - security standards and reports",
            isUser: false,
            timestamp: new Date()
        }
    ]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            text: input,
            isUser: true,
            timestamp: new Date()
        };

        const query = {
            question: input,
            response_length: "concise"
        };

        try {
            setMessages(prev => [...prev, userMessage]);
            setIsTyping(true);
            setInput('');

            const response = await baseapi.post('/chat_bot/ask', query);

            if (response.data?.status) {
                setTimeout(() => {
                    const botMessage: Message = {
                        id: (Date.now() + 1).toString(),
                        // UPDATED: Now accesses response.data.data.answer
                        text: response.data.data.answer,
                        isUser: false,
                        // UPDATED: Uses new Date() since timestamp isn't in your JSON payload
                        timestamp: new Date()
                    };
                    setMessages(prev => [...prev, botMessage]);
                    setIsTyping(false);
                }, 1500);
            }
        } catch (error) {
            console.error('Error fetching answer:', error);
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="fixed bottom-24 right-4 lg:right-10 w-[22rem] lg:w-[30rem] h-[30rem] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col z-50 overflow-hidden"
                >
                    {/* Header */}
                    <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 rounded-t-xl border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm">
                                <img src="/aiowl.png" alt="Owl" className="w-6 h-6 object-contain" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Ovul AI Assistant</h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Online</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1 rounded-lg hover:bg-gray-200/60 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 custom-scroll space-y-4 bg-gray-50/50 dark:bg-gray-900/40">
                        {messages.map((message) => (
                            <div key={message.id} className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
                                <div className="flex items-start space-x-2 max-w-[85%]">
                                    {!message.isUser && (
                                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                                            <img src="/owllogopng.png" alt="Bot" className="w-full h-full object-contain" />
                                        </div>
                                    )}
                                    <div
                                        className={`px-3 py-2 rounded-xl text-[12px] shadow-sm ${message.isUser
                                            ? 'bg-orange-500 text-white rounded-br-sm'
                                            : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm border border-gray-200 dark:border-gray-600'
                                            }`}
                                    >
                                        <div className="prose dark:prose-invert max-w-none text-[12px] leading-relaxed text-gray-800 dark:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-white prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
                                            <Markdown>{message.text}</Markdown>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="flex items-start space-x-2">
                                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                                        <img src="/owllogopng.png" alt="Bot typing" className="w-full h-full object-contain" />
                                    </div>
                                    <div className="bg-white dark:bg-gray-700 px-4 py-3 rounded-xl rounded-bl-sm border border-gray-200 dark:border-gray-600 shadow-sm">
                                        <div className="flex space-x-1.5">
                                            <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-400 rounded-full animate-bounce"></div>
                                            <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                                            <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Message Ovul Ai..."
                            className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:border-orange-500 transition-all placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
                        />
                        <button
                            onClick={handleSend}
                            disabled={!input.trim() || isTyping}
                            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:opacity-50 text-white p-2.5 rounded-lg transition-colors duration-200 flex-shrink-0 cursor-pointer disabled:cursor-not-allowed shadow-sm"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};